import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { openSignedUrl } from '@/lib/openSignedUrl';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { ListPagination } from '@/components/ListPagination';
import { useServerPagination } from '@/hooks/useServerPagination';
import {
    completionTime,
    effectiveContractId,
    firstEmbed,
    type CountedSignature,
    type DepositArcEmbed,
    type Embedded,
} from '@/lib/agreements/documents';
import { driverProfileName } from '@/lib/driverProfile';
import { matchesAgreementSearch, parseAgreementSearch } from '@/lib/agreements/referenceCheck';
import { fetchAgreementDocumentByReference } from '@/lib/agreements/api';
import { stepForDocType } from '@/components/agreements/boardSlots';
import { areaPath } from '@/components/contracts/setup/steps';
import type { AgreementDocType } from '@/lib/agreements/constants';
import { Search, ExternalLink } from 'lucide-react';

/** One `agreement_documents` row of the awaiting-finalization backlog, before its derivations. */
interface AwaitingDocument {
    id: number;
    contract_id: number | null;
    doc_type: string;
    reference_no: string;
    agreement_signatures: CountedSignature[] | null;
    contract_deposit_agreements?: Embedded<DepositArcEmbed>;
}

/**
 * "Awaiting finalization" is a real state, not a filter over the legacy rows.
 *
 * A document is COMPLETED the moment its last party signs, but the legacy `signed_at` and
 * `agreement_url` are only written once the executed PDF exists in storage — so between
 * those two moments the contract has a fully signed agreement and nothing in
 * `contract_agreements` to show for it. The countersigning admin's own session normally
 * closes that gap within seconds; this view is what finds the ones where it did not,
 * because the tab was closed or the render failed.
 */
type RecordsView = 'all' | 'awaiting';

/**
 * THE DRIVER'S NAME IS NOT A COLUMN OF `contract_agreements`, so searching for one is an inner
 * join and a filter on the far end of it — the shape `DriversRecords` already uses, one hop
 * deeper. `!inner` ON EVERY HOP IS THE LOAD-BEARING PART: without it PostgREST applies the filter
 * to the embedded object only, and every agreement in the table comes back with an empty
 * `contracts` on the ones that do not match. The vehicle stays a left join deliberately — a
 * contract with no vehicle yet still has an agreement, and it is not what is being searched for.
 *
 * ONE STRING LITERAL EACH, NEVER A CONCATENATION. supabase-js parses the select string AS A TYPE
 * to shape `data`; `'a' + 'b'` widens to `string`, which it answers with `GenericStringError` and
 * every field on the row then fails to exist. Same reason these are interpolated straight into
 * `.select(...)` below rather than assembled into a local first.
 */
const CONTRACT_EMBED = 'contracts(id, drivers(profiles(id, admin_id, first_name, last_name)), vehicles(vehicle_registrations(number)))';
const CONTRACT_EMBED_INNER = 'contracts!inner(id, drivers!inner(profiles!inner(id, admin_id, first_name, last_name)), vehicles(vehicle_registrations(number)))';
/** Where the name filter lands: PostgREST addresses an embedded filter by its path in the select. */
const DRIVER_NAME_PATH = 'contracts.drivers.profiles';

const CHECK_DIGIT_MESSAGE_ID = 'agreement-search-check-digit';

export default function ContractAgreementsRecords() {
    const [searchTerm, setSearchTerm] = useState('');
    const [view, setView] = useState<RecordsView>('all');
    const debouncedSearch = useDebounce(searchTerm, 300);
    const search = useMemo(() => parseAgreementSearch(debouncedSearch), [debouncedSearch]);

    /**
     * A REFERENCE NAMES A DOCUMENT, AND THIS LIST IS NOT MADE OF DOCUMENTS — it is made of the
     * legacy `contract_agreements` rows a finalized document stamps. So a reference is resolved
     * once, here, into the id of the row it became, and both queries below filter on that id.
     *
     * NULL IS A REAL ANSWER AND IT IS NOT AN ERROR. It means no row on this list corresponds:
     * either no document carries that reference, or the one that does has not been finalized
     * (its `contract_agreement_id` is still null), or it is a deposit, loan or insurance-loan
     * document, whose arcs land in their own tables and never in this one. The unfinalized case
     * is exactly what the "awaiting finalization" view lists, and the same reference typed there
     * finds it — the two views between them cover the document's whole life.
     */
    const referenceNo = search.kind === 'reference' ? search.referenceNo : null;
    const { data: referencedAgreementId } = useQuery({
        queryKey: ['contract-agreement-by-reference', referenceNo],
        queryFn: async () => {
            const document = await fetchAgreementDocumentByReference(referenceNo!);
            return document?.contract_agreement_id ?? null;
        },
        enabled: referenceNo !== null,
    });

    /** Nothing is queried on a reference until it has been resolved, or the page would count all. */
    const referenceResolved = referenceNo === null || referencedAgreementId !== undefined;

    /**
     * The two searches that are answered without a round trip: one whose check digit says the
     * number was misread, and one whose reference resolved to nothing on this list.
     */
    const searchIsUnanswerable =
        search.kind === 'failed-check-digit' ||
        (search.kind === 'reference' && referencedAgreementId === null);

    const { data: countData } = useQuery({
        queryKey: ['contract-agreements-count', debouncedSearch, referencedAgreementId],
        queryFn: async () => {
            if (searchIsUnanswerable) return 0;
            /*
             * THE COUNT AND THE PAGE MUST BE FILTERED IDENTICALLY. This count is what
             * `useServerPagination` slices into ranges, so a filter applied to one and not the
             * other prints page numbers for rows the other query will never return.
             */
            let query = supabase
                .from('contract_agreements')
                .select(search.kind === 'text' ? `*, ${CONTRACT_EMBED_INNER}` : '*', {
                    count: 'exact',
                    head: true,
                });
            if (search.kind === 'text') {
                query = query.or(
                    `first_name.ilike.%${search.text}%,last_name.ilike.%${search.text}%`,
                    { referencedTable: DRIVER_NAME_PATH },
                );
            } else if (search.kind === 'contract') {
                query = query.eq('contract_id', search.contractId);
            } else if (search.kind === 'reference' && referencedAgreementId != null) {
                query = query.eq('id', referencedAgreementId);
            }
            const { count, error } = await query;
            if (error) throw error;
            return count || 0;
        },
        enabled: referenceResolved,
    });

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage } = useServerPagination({ totalCount: countData || 0 });

    useEffect(() => { resetPage(); }, [debouncedSearch]);

    const { data: agreements, isLoading } = useQuery({
        queryKey: ['contract-agreements', currentPage, debouncedSearch, referencedAgreementId],
        queryFn: async () => {
            if (searchIsUnanswerable) return [];
            const embed = search.kind === 'text' ? CONTRACT_EMBED_INNER : CONTRACT_EMBED;
            let query = supabase.from('contract_agreements').select(`*, ${embed}`);
            if (search.kind === 'text') {
                query = query.or(
                    `first_name.ilike.%${search.text}%,last_name.ilike.%${search.text}%`,
                    { referencedTable: DRIVER_NAME_PATH },
                );
            } else if (search.kind === 'contract') {
                query = query.eq('contract_id', search.contractId);
            } else if (search.kind === 'reference' && referencedAgreementId != null) {
                query = query.eq('id', referencedAgreementId);
            }
            const { data, error } = await query
                .order('signed_at', { ascending: false })
                .range(from, to);
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined && view === 'all',
    });

    /**
     * BOTH COLUMNS THIS LIST USED TO SELECT ARE GONE, AND SO IS THE ORDER BY.
     *
     * `agreement_documents.title` and `.completed_at` stopped being columns — the title lives
     * only on the pinned template and nothing here prints it, and the completion time is now
     * derived from the signature rows. PostgREST answers a select naming a dropped column with
     * a 42703, so the old query did not degrade: this whole view failed.
     *
     * THE DERIVATIONS ARE `documents.ts`'s, NOT RESTATED HERE. `get_agreement_documents` applies
     * both in SQL, but it is keyed on ONE contract and this backlog is keyed on none, so the
     * rules are shared with the deposit sheet through that module rather than copied.
     *
     * THE QUERY STILL ORDERS, ON A COLUMN THAT SURVIVED. `completed_at` is derived now and
     * PostgREST cannot sort by an aggregate over an embedded table, so the final order is the
     * JS sort below — but a query with no ORDER BY at all returns rows in whatever order the
     * planner produced them, and anything the server caps is then an arbitrary subset rather
     * than the oldest work. `created_at` moves in the same direction as the completion it
     * precedes, so it is both a deterministic page and a sane tiebreak: `Array#sort` is stable,
     * so documents that share a completion time (or have none yet) keep it.
     *
     * A DEPOSIT AGREEMENT MAY BELONG TO NOBODY, and it can sit in this backlog like any other —
     * the payer signs by link, an admin countersigns, and the finalize is what did not land. So
     * the contract is resolved through the arc and the row names a destination that exists even
     * when there is no contract at all.
     */
    const { data: awaiting, isLoading: awaitingLoading } = useQuery({
        queryKey: ['contract-agreements-awaiting-finalization'],
        queryFn: async () => {
            const { data, error } = await (supabase as any)
                .from('agreement_documents')
                .select(
                    'id, contract_id, doc_type, reference_no, ' +
                    'agreement_signatures(status, signed_at), ' +
                    'contract_deposit_agreements(contract_deposits(contract_id))',
                )
                .eq('status', 'COMPLETED')
                .is('finalized_at', null)
                .order('created_at', { ascending: true });
            if (error) throw error;
            return ((data ?? []) as AwaitingDocument[])
                .map((document) => ({
                    id: document.id,
                    doc_type: document.doc_type,
                    reference_no: document.reference_no,
                    contract_id: effectiveContractId(document),
                    completed_at: completionTime(document.agreement_signatures ?? []),
                }))
                // Oldest first: the one that has been waiting longest is the one to chase.
                .sort((a, b) => (a.completed_at ?? '').localeCompare(b.completed_at ?? ''));
        },
        enabled: view === 'awaiting',
    });

    /**
     * THIS LIST ALREADY HOLDS EVERY ROW IT WILL EVER SHOW. The backlog is unpaginated and fetched
     * whole, so its search is a filter over what is in hand rather than a second round trip — and
     * unlike the list above, these rows carry the reference itself, so a reference matches the
     * very column that is printed in the Reference cell.
     */
    const matchingAwaiting = useMemo(
        () => (awaiting ?? []).filter((document) => matchesAgreementSearch(document, search)),
        [awaiting, search],
    );

    const failedCheckDigit = search.kind === 'failed-check-digit' ? search.typed : null;

    return (
        <Card className="border-0">
            <CardHeader>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder={
                                view === 'awaiting'
                                    ? 'Reference, contract # or type'
                                    : 'Reference, driver or contract #'
                            }
                            className={cn('pl-9', failedCheckDigit && 'border-destructive')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            aria-invalid={!!failedCheckDigit}
                            aria-describedby={failedCheckDigit ? CHECK_DIGIT_MESSAGE_ID : undefined}
                        />
                    </div>
                    <Select value={view} onValueChange={(value) => setView(value as RecordsView)}>
                        <SelectTrigger className="w-full sm:w-56">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All agreements</SelectItem>
                            <SelectItem value="awaiting">Awaiting finalization</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {/* Every reference carries a check digit — there is only the one shape — so one
                    that fails is a number that was misread, and saying so is the difference
                    between "you have this wrong" and an empty list, which reads as "we have no
                    such agreement". */}
                {failedCheckDigit ? (
                    <p id={CHECK_DIGIT_MESSAGE_ID} className="text-xs text-destructive">
                        {failedCheckDigit} fails its check digit — re-read it. A reference that
                        fails is a misread number, not a missing agreement.
                    </p>
                ) : null}
            </CardHeader>
            <CardContent>
                {view === 'awaiting' ? (
                    awaitingLoading ? (
                        <Skeleton className="h-48 w-full" />
                    ) : matchingAwaiting.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Completed</TableHead>
                                    <TableHead>Contract</TableHead>
                                    <TableHead>Reference</TableHead>
                                    <TableHead>Document</TableHead>
                                    <TableHead>Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {matchingAwaiting.map((document) => (
                                    <TableRow key={document.id}>
                                        <TableCell>
                                            {document.completed_at ? new Date(document.completed_at).toLocaleDateString() : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {document.contract_id ? `#${document.contract_id}` : 'Unassigned deposit'}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">{document.reference_no}</TableCell>
                                        <TableCell><Badge variant="outline">{document.doc_type}</Badge></TableCell>
                                        <TableCell>
                                            {/* A document with no contract is a deposit agreement signed before one
                                                existed, and the deposit sheet is where its Finalize lives.
                                                EVERY OTHER TYPE GOES TO ITS OWN AREA: this link sent all five to
                                                the agreements board, where a deposit or insurance-loan document is
                                                not shown at all. */}
                                            <Link
                                                to={
                                                    document.contract_id
                                                        ? areaPath(
                                                            document.contract_id,
                                                            stepForDocType(document.doc_type as AgreementDocType),
                                                        )
                                                        : '/dashboard/contracts/deposits'
                                                }
                                                className="text-primary hover:underline"
                                            >
                                                {document.contract_id
                                                    ? 'Finalize on the signing board'
                                                    : 'Finalize on the deposit'}
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <p className="py-8 text-center text-muted-foreground">
                            {search.kind === 'empty'
                                ? 'Nothing is waiting to be finalized.'
                                : 'Nothing waiting to be finalized matches that search.'}
                        </p>
                    )
                ) : (
                    <>
                        <ListPagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} />
                        {/* A reference still being resolved leaves BOTH list queries disabled, and a
                            disabled query is not "loading" — without this the page answers an
                            in-flight lookup with "no agreement matches", which is the one thing it
                            cannot yet know. */}
                        {isLoading || !referenceResolved ? (
                            <Skeleton className="h-48 w-full" />
                        ) : agreements && agreements.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Signed</TableHead>
                                        <TableHead>Contract</TableHead>
                                        <TableHead>Driver</TableHead>
                                        <TableHead>Vehicle</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Document</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {agreements.map((a) => {
                                        /*
                                         * THE EMBEDS ARE READ THROUGH A HELPER, NOT OFF THE
                                         * OBJECT. `profiles` hangs off a foreign key pointing AT
                                         * `drivers`, so PostgREST hands it back as a collection —
                                         * `profiles.first_name` was undefined on every row and
                                         * this column printed a blank name, not a dash. Now that
                                         * the box searches for these names, a cell that never
                                         * shows one would make every hit unverifiable.
                                         *
                                         * AND NOT `firstEmbed` FOR THIS ONE: the collection can
                                         * hold an admin's test profile beside the driver's, and
                                         * position does not say which is which. `driverProfileName`
                                         * applies the same `admin_id IS NULL` exclusion the server
                                         * does, and answers null — a dash — where the driver has
                                         * no row of their own.
                                         */
                                        const driver = driverProfileName(a.contracts?.drivers?.profiles) ?? '-';
                                        /* The plate is `vehicle_registrations.number`; there is no
                                           `registration_number` column on `vehicles` and never was. */
                                        const plate = firstEmbed(
                                            firstEmbed(a.contracts?.vehicles)?.vehicle_registrations,
                                        )?.number;
                                        return (
                                            <TableRow key={a.id}>
                                                <TableCell>{a.signed_at ? new Date(a.signed_at).toLocaleDateString() : '-'}</TableCell>
                                                <TableCell>#{a.contract_id}</TableCell>
                                                <TableCell>{driver}</TableCell>
                                                <TableCell>{plate || '-'}</TableCell>
                                                <TableCell><Badge variant="outline">{a.type}</Badge></TableCell>
                                                <TableCell>
                                                    {a.agreement_url ? (
                                                        <button onClick={() => openSignedUrl(a.agreement_url!)} className="text-primary hover:underline flex items-center gap-1">
                                                            View <ExternalLink className="h-3 w-3" />
                                                        </button>
                                                    ) : '-'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        ) : (
                            <p className="text-center text-muted-foreground py-8">
                                {search.kind === 'empty'
                                    ? 'No agreements found'
                                    : 'No agreement matches that search'}
                            </p>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
